'use client';

import React, { useState } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { 
  DollarSign, 
  Clock, 
  User, 
  Scissors, 
  ShoppingBag, 
  Tag, 
  CreditCard, 
  Plus, 
  CheckCircle2, 
  AlertCircle,
  Calendar
} from 'lucide-react';
import { TodayAppointment } from './ChargeClientModal';

interface ChargeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  dateLabel: string;
  todayAppointments: TodayAppointment[];
  onOpenLooseAppointment: () => void;
  onChargeAppointment: (
    appointment: TodayAppointment,
    channel: string,
    voucherCode?: string,
    discount?: number
  ) => Promise<void>;
  onChargeProducts: (payload: any) => Promise<void>;
  products: any[];
}

const PAYMENT_METHODS = [
  { id: 'EFECTIVO', label: 'Efectivo', icon: DollarSign },
  { id: 'MERCADOPAGO', label: 'Mercado Pago', icon: CreditCard },
  { id: 'TRANSFERENCIA', label: 'Transferencia', icon: CreditCard },
  { id: 'DEBITO', label: 'Débito', icon: CreditCard },
  { id: 'CREDITO', label: 'Crédito', icon: CreditCard },
];

export const ChargeDrawer: React.FC<ChargeDrawerProps> = ({
  isOpen,
  onClose,
  dateLabel,
  todayAppointments,
  onOpenLooseAppointment,
  onChargeAppointment,
  onChargeProducts,
  products
}) => {
  const [activeTab, setActiveTab] = useState<'turnos' | 'productos'>('turnos');
  
  // Estado para turno seleccionado
  const [selectedApptId, setSelectedApptId] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState('EFECTIVO');
  const [submitting, setSubmitting] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<number>(0);
  const [voucherStatus, setVoucherStatus] = useState<string | null>(null);

  // Estado para venta de mostrador
  const [productCart, setProductCart] = useState<Array<{ id: string; name: string; price: number; quantity: number }>>([]);
  const [counterClientName, setCounterClientName] = useState('');
  const [counterChannel, setCounterChannel] = useState('EFECTIVO');

  const selectedAppt = todayAppointments.find((a) => a.id === selectedApptId) || todayAppointments[0] || null;

  const handleApplyVoucher = () => {
    if (!voucherCode.trim()) return;
    // Simulación de voucher / gift card de $2.000 o código promocional
    if (voucherCode.toUpperCase() === 'PROMO20' || voucherCode.toUpperCase().startsWith('GFT-')) {
      setAppliedDiscount(2000);
      setVoucherStatus('✓ Voucher aplicado (-$2.000)');
    } else {
      setVoucherStatus('Código inválido o vencido');
      setAppliedDiscount(0);
    }
  };

  const handleConfirmChargeAppt = async () => {
    if (!selectedAppt) return;
    setSubmitting(true);
    try {
      await onChargeAppointment(
        selectedAppt,
        selectedChannel,
        voucherCode.trim() || undefined,
        appliedDiscount
      );
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddProduct = (prod: any) => {
    setProductCart((prev) => {
      const existing = prev.find((p) => p.id === prod.id);
      if (existing) {
        return prev.map((p) => (p.id === prod.id ? { ...p, quantity: p.quantity + 1 } : p));
      }
      return [...prev, { id: prod.id, name: prod.name, price: prod.price, quantity: 1 }];
    });
  };

  const handleRemoveProduct = (id: string) => {
    setProductCart((prev) => prev.filter((p) => p.id !== id));
  };

  const handleConfirmProductSale = async () => {
    if (productCart.length === 0) return;
    setSubmitting(true);
    try {
      await onChargeProducts({
        clientName: counterClientName.trim() || 'Cliente Mostrador',
        paymentChannel: counterChannel,
        items: productCart.map((p) => ({
          productId: p.id,
          name: p.name,
          quantity: p.quantity,
          unitPrice: p.price,
        })),
      });
      setProductCart([]);
      setCounterClientName('');
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const cartTotal = productCart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Cobro en Mostrador y Caja"
      subtitle={`Gestión de cobro para ${dateLabel}`}
    >
      <div className="flex flex-col h-full">
        {/* Selector de Pestañas: Cobro de Turno vs Venta Mostrador */}
        <div className="flex items-center border-b border-slate-200 bg-slate-50/60 px-5 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('turnos')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'turnos'
                ? 'border-[#6255F5] text-[#6255F5]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Cobrar Turno ({todayAppointments.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('productos')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'productos'
                ? 'border-[#6255F5] text-[#6255F5]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>Venta de Productos ({productCart.length})</span>
          </button>
        </div>

        {/* Contenido scrolleable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {activeTab === 'turnos' ? (
            <div className="space-y-4">
              {todayAppointments.length === 0 ? (
                <div className="p-8 text-center border-2 border-dashed border-slate-200 rounded-2xl space-y-3">
                  <Calendar className="w-8 h-8 text-slate-300 mx-auto" />
                  <p className="text-xs font-bold text-slate-700">No hay turnos agendados para cobrar hoy</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onClose();
                      onOpenLooseAppointment();
                    }}
                  >
                    + Registrar Turno Suelto (Sobreturno)
                  </Button>
                </div>
              ) : (
                <>
                  {/* Selector de Turno a Cobrar */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      Seleccionar Turno del Día
                    </label>
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {todayAppointments.map((appt) => {
                        const isSelected = (selectedAppt?.id || todayAppointments[0]?.id) === appt.id;
                        return (
                          <div
                            key={appt.id}
                            onClick={() => setSelectedApptId(appt.id)}
                            className={`p-3 rounded-xl border transition cursor-pointer flex items-center justify-between ${
                              isSelected
                                ? 'bg-violet-50/50 border-[#6255F5] shadow-xs'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div>
                              <p className="text-xs font-bold text-slate-900">
                                {appt.clientName}
                              </p>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                {appt.startTime} hs · {appt.serviceName} ({appt.staffName})
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-xs font-black text-slate-900 block">
                                ${appt.price?.toLocaleString('es-AR')}
                              </span>
                              {appt.depositAmount > 0 && (
                                <span className="text-[10px] text-emerald-600 font-bold block">
                                  Seña: -${appt.depositAmount.toLocaleString('es-AR')}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Resumen Financiero del Turno Seleccionado */}
                  {selectedAppt && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span>Precio Total del Servicio:</span>
                        <strong className="text-slate-900">${selectedAppt.price?.toLocaleString('es-AR')}</strong>
                      </div>
                      {selectedAppt.depositAmount > 0 && (
                        <div className="flex items-center justify-between text-xs text-emerald-700">
                          <span>Seña abonada (Mercado Pago):</span>
                          <strong>-${selectedAppt.depositAmount?.toLocaleString('es-AR')}</strong>
                        </div>
                      )}
                      {appliedDiscount > 0 && (
                        <div className="flex items-center justify-between text-xs text-[#6255F5]">
                          <span>Descuento / Gift Card:</span>
                          <strong>-${appliedDiscount.toLocaleString('es-AR')}</strong>
                        </div>
                      )}
                      <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-sm font-black text-slate-900">
                        <span>Total a Cobrar en Salón:</span>
                        <span className="text-[#6255F5]">
                          ${Math.max(0, (selectedAppt.price || 0) - (selectedAppt.depositAmount || 0) - appliedDiscount).toLocaleString('es-AR')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Cupón / Gift Card */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-700">
                      Gift Card / Cupón de Descuento
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                        placeholder="ej: GFT-8492 o PROMO20"
                        className="font-mono uppercase"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleApplyVoucher}
                      >
                        Aplicar
                      </Button>
                    </div>
                    {voucherStatus && (
                      <p className={`text-[11px] font-bold ${appliedDiscount > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {voucherStatus}
                      </p>
                    )}
                  </div>

                  {/* Selector de Método de Pago */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-700">
                      Método de Pago Recibido
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {PAYMENT_METHODS.map((m) => {
                        const Icon = m.icon;
                        const isSel = selectedChannel === m.id;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => setSelectedChannel(m.id)}
                            className={`p-2.5 rounded-xl border text-xs font-bold flex items-center gap-2 transition cursor-pointer ${
                              isSel
                                ? 'bg-[#6255F5] text-white border-[#6255F5] shadow-xs'
                                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            <span>{m.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* TAB PRODUCTOS: VENTA DE MOSTRADOR */
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Nombre del Cliente (Mostrador)
                </label>
                <Input
                  value={counterClientName}
                  onChange={(e) => setCounterClientName(e.target.value)}
                  placeholder="ej: Cliente Ocasional / Florencia"
                />
              </div>

              {/* Catálogo de Productos para Agregar */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">
                  Agregar Productos al Carrito
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {products.map((prod) => (
                    <button
                      key={prod.id}
                      type="button"
                      onClick={() => handleAddProduct(prod)}
                      className="p-2.5 rounded-xl border border-slate-200 hover:border-[#6255F5] hover:bg-violet-50/30 transition text-left cursor-pointer flex items-center justify-between"
                    >
                      <div className="min-w-0 pr-1">
                        <p className="text-xs font-bold text-slate-800 truncate">{prod.name}</p>
                        <p className="text-[10px] text-slate-400">Stock: {prod.stock ?? 10}</p>
                      </div>
                      <span className="text-xs font-black text-slate-900 shrink-0">
                        ${prod.price?.toLocaleString('es-AR')}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Carrito de Productos */}
              {productCart.length > 0 && (
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                  <span className="text-xs font-bold text-slate-800 block">
                    Productos Seleccionados
                  </span>
                  <div className="divide-y divide-slate-200/60">
                    {productCart.map((item) => (
                      <div key={item.id} className="py-2 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-bold text-slate-800">{item.name}</span>
                          <span className="text-slate-400 text-[11px] block">
                            {item.quantity} x ${item.price?.toLocaleString('es-AR')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-black text-slate-900">
                            ${(item.price * item.quantity).toLocaleString('es-AR')}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveProduct(item.id)}
                            className="text-rose-500 hover:text-rose-700 text-xs font-bold px-1"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-slate-200 flex items-center justify-between font-black text-sm text-slate-900">
                    <span>Total Productos:</span>
                    <span className="text-[#6255F5]">${cartTotal.toLocaleString('es-AR')}</span>
                  </div>
                </div>
              )}

              {/* Método de Pago para Productos */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">
                  Método de Pago
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((m) => {
                    const Icon = m.icon;
                    const isSel = counterChannel === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setCounterChannel(m.id)}
                        className={`p-2.5 rounded-xl border text-xs font-bold flex items-center gap-2 transition cursor-pointer ${
                          isSel
                            ? 'bg-[#6255F5] text-white border-[#6255F5] shadow-xs'
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <span>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Sticky */}
        <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-between shrink-0">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          {activeTab === 'turnos' ? (
            <Button
              variant="primary"
              onClick={handleConfirmChargeAppt}
              disabled={!selectedAppt || submitting}
              isLoading={submitting}
            >
              Registrar Cobro
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleConfirmProductSale}
              disabled={productCart.length === 0 || submitting}
              isLoading={submitting}
            >
              Cobrar Venta (${cartTotal.toLocaleString('es-AR')})
            </Button>
          )}
        </div>
      </div>
    </SlideOverDrawer>
  );
};
